import type { components } from '@fluencelabs/vodopad-admin-api'

import { isInternalPricingGroup } from '../pricingGroups'

export type AdminCharges = components['schemas']['AdminChargesDto']
type ChargeBucket = components['schemas']['ChargeBucketDto']

const MANUAL_CHARGE_PRODUCT = 'manual-charge'
const CPU_CLOUD_PRODUCT = 'cpu-cloud'
const CPU_RESOURCE_LABELS: Record<string, string> = {
  vm: 'CPU VMs',
  storage: 'CPU storage',
  public_ip: 'CPU public IPs',
}

/** GPU resale sends no product type; any other source is shown under its own product type. */
export function chargeCategory(bucket: ChargeBucket): string {
  const product = bucket.productType
  if (!product) return 'GPU'
  if (product === MANUAL_CHARGE_PRODUCT) return 'Manual charges'
  if (product === CPU_CLOUD_PRODUCT) {
    return CPU_RESOURCE_LABELS[bucket.resourceType ?? ''] ?? 'CPU other'
  }
  return product
}

export interface ChargePoint {
  period: string
  category: string
  value: number
}

export interface ChargeTotal {
  key: string
  cents: number
}

/** Amounts are exact cent strings; summing cents keeps totals exact. */
export function toCents(amountUsd: string): number {
  const [whole, fraction = ''] = amountUsd.split('.')
  const sign = whole.startsWith('-') ? -1 : 1
  const cents = (fraction + '00').slice(0, 2)
  return sign * (Math.abs(Number(whole)) * 100 + Number(cents))
}

function totalsBy(
  buckets: ChargeBucket[],
  key: (bucket: ChargeBucket) => string,
): ChargeTotal[] {
  const totals = new Map<string, number>()
  for (const bucket of buckets) {
    const k = key(bucket)
    totals.set(k, (totals.get(k) ?? 0) + toCents(bucket.amountUsd))
  }
  return [...totals]
    .map(([k, cents]) => ({ key: k, cents }))
    .sort((a, b) => b.cents - a.cents)
}

export function chargeSeries(charges: AdminCharges): ChargePoint[] {
  const points = new Map<
    string,
    { period: string; category: string; cents: number }
  >()
  for (const bucket of charges.buckets) {
    const category = chargeCategory(bucket)
    const id = `${bucket.periodStart}|${category}`
    const point = points.get(id) ?? {
      period: bucket.periodStart,
      category,
      cents: 0,
    }
    point.cents += toCents(bucket.amountUsd)
    points.set(id, point)
  }
  return [...points.values()]
    .map(({ period, category, cents }) => ({
      period,
      category,
      value: cents / 100,
    }))
    .sort((a, b) => a.period.localeCompare(b.period))
}

export const chargesByCategory = (charges: AdminCharges) =>
  totalsBy(charges.buckets, chargeCategory)

export const chargesByCluster = (charges: AdminCharges) =>
  totalsBy(charges.buckets, (bucket) => bucket.clusterId ?? '')

export type Audience = 'Customers' | 'Internal' | 'Unknown account'

export function chargeAudience(pricingGroup?: string | null): Audience {
  if (!pricingGroup) return 'Unknown account'
  return isInternalPricingGroup(pricingGroup) ? 'Internal' : 'Customers'
}

export const chargesByPricingGroup = (charges: AdminCharges) =>
  totalsBy(charges.buckets, (bucket) => bucket.pricingGroup ?? '')
